import React, { useState, useEffect, ChangeEvent, FormEvent, useCallback } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
    FormControl, InputLabel, Select, MenuItem, Card, CardContent, CardHeader,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton,
    CircularProgress, Paper, Typography, SelectChangeEvent,
    Tabs, Tab, Tooltip
} from '@mui/material';
import { Edit, Delete, Add, ContentCopy, VpnKey } from '@mui/icons-material';
import Loader from '../Loader';
import { useToast } from '../../hooks/useToast';
import { ToastNotification } from '../common/ToastNotification';
import { formatApiError } from '../../utils/errorFormatter';

// Определения типов
interface Group {
    id: number;
    name: string;
    info: string;
    printInfo: string;
}

interface Terminal {
    id: number;
    groupId: number;
    internalId: number;
    name: string;
    info: string;
    blocked: number | null;
    timeZone: number;
    apiKey: string;
}

const timezones = [
    { value: -12, label: '(UTC-12:00) Международная линия смены дат' },
    { value: -11, label: '(UTC-11:00) Самоа' },
    { value: -10, label: '(UTC-10:00) Гавайи' },
    { value: -9, label: '(UTC-09:00) Аляска' },
    { value: -8, label: '(UTC-08:00) Тихоокеанское время (США и Канада)' },
    { value: -7, label: '(UTC-07:00) Горное время (США и Канада)' },
    { value: -6, label: '(UTC-06:00) Центральное время (США и Канада)' },
    { value: -5, label: '(UTC-05:00) Восточное время (США и Канада)' },
    { value: -4, label: '(UTC-04:00) Атлантическое время (Канада)' },
    { value: -3, label: '(UTC-03:00) Буэнос-Айрес' },
    { value: -2, label: '(UTC-02:00) Среднеатлантическое время' },
    { value: -1, label: '(UTC-01:00) Азорские острова' },
    { value: 0, label: '(UTC+00:00) Гринвич' },
    { value: 1, label: '(UTC+01:00) Центральная Европа' },
    { value: 2, label: '(UTC+02:00) Восточная Европа' },
    { value: 3, label: '(UTC+03:00) Москва, Санкт-Петербург' },
    { value: 4, label: '(UTC+04:00) Самара, Ижевск' },
    { value: 5, label: '(UTC+05:00) Екатеринбург, Ташкент' },
    { value: 6, label: '(UTC+06:00) Омск, Астана' },
    { value: 7, label: '(UTC+07:00) Красноярск, Бангкок' },
    { value: 8, label: '(UTC+08:00) Иркутск, Пекин' },
    { value: 9, label: '(UTC+09:00) Якутск, Токио' },
    { value: 10, label: '(UTC+10:00) Владивосток, Гуам' },
    { value: 11, label: '(UTC+11:00) Магадан, Соломоновы о-ва' },
    { value: 12, label: '(UTC+12:00) Камчатка, Окленд' },
    { value: 13, label: '(UTC+13:00) Нукуалофа' }
];

const defaultNewGroup = { name: '', info: '', printInfo: 'Код для выполнения возврата: {0}' };

export const TerminalManagement: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [groups, setGroups] = useState<Group[]>([]);
    const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
    const [terminals, setTerminals] = useState<Terminal[]>([]);
    const [terminalsLoading, setTerminalsLoading] = useState(false);

    // Состояния для модальных окон групп
    const [groupModal, setGroupModal] = useState(false);
    const [editGroupModal, setEditGroupModal] = useState(false);
    const [deleteGroupModal, setDeleteGroupModal] = useState(false);
    const [newGroup, setNewGroup] = useState(defaultNewGroup);
    const [editingGroup, setEditingGroup] = useState<Group | null>(null);
    const [deletingGroup, setDeletingGroup] = useState<Group | null>(null);

    // Состояния для модальных окон терминалов
    const [terminalModal, setTerminalModal] = useState(false);
    const [editTerminalModal, setEditTerminalModal] = useState(false);
    const [deleteTerminalModal, setDeleteTerminalModal] = useState(false);
    const [generateKeyModal, setGenerateKeyModal] = useState(false);
    const [apiKeyModal, setApiKeyModal] = useState(false);
    const [newTerminal, setNewTerminal] = useState({ name: '', info: '', blocked: '', timeZone: '3' });
    const [editingTerminal, setEditingTerminal] = useState<Terminal | null>(null);
    const [deletingTerminal, setDeletingTerminal] = useState<Terminal | null>(null);
    const [generatingKeyTerminal, setGeneratingKeyTerminal] = useState<Terminal | null>(null);
    const [apiKeyTerminal, setApiKeyTerminal] = useState<Terminal | null>(null);

    const { toast, showError, showSuccess, handleClose } = useToast();

    // Переключатели модальных окон
    const toggleGroupModal = () => setGroupModal(!groupModal);
    const toggleEditGroupModal = () => setEditGroupModal(!editGroupModal);
    const toggleDeleteGroupModal = () => setDeleteGroupModal(!deleteGroupModal);
    const toggleTerminalModal = () => setTerminalModal(!terminalModal);
    const toggleEditTerminalModal = () => setEditTerminalModal(!editTerminalModal);
    const toggleDeleteTerminalModal = () => setDeleteTerminalModal(!deleteTerminalModal);
    const toggleGenerateKeyModal = () => setGenerateKeyModal(!generateKeyModal);
    const toggleApiKeyModal = () => setApiKeyModal(!apiKeyModal);

    // --- Функции для работы с API (обернуты в useCallback) ---
    const fetchGroups = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/management/groups');
            if (!response.ok) {
                const errorMsg = await formatApiError(response, 'Не удалось загрузить группы');
                throw new Error(errorMsg);
            }
            const data = await response.json();
            setGroups(data);
            if (data.length > 0 && selectedGroupId === null) {
                setSelectedGroupId(data[0].id);
            } else if (data.length === 0) {
                setSelectedGroupId(null);
            }
            return data;
        } catch (error: any) {
            showError(error.message || 'Произошла ошибка при загрузке групп');
            return [];
        } finally {
            setLoading(false);
        }
    }, [selectedGroupId, showError]);

    const fetchTerminals = useCallback(async (groupId: number) => {
        setTerminalsLoading(true);
        try {
            const response = await fetch(`/api/management/terminals/${groupId}`);
            if (!response.ok) {
                const errorMsg = await formatApiError(response, 'Не удалось загрузить терминалы');
                throw new Error(errorMsg);
            }
            const data = await response.json();
            setTerminals(data);
        } catch (error: any) {
            showError(error.message || 'Произошла ошибка при загрузке терминалов');
        } finally {
            setTerminalsLoading(false);
        }
    }, [showError]);

    // --- Эффекты ---
    useEffect(() => {
        fetchGroups();
    }, [fetchGroups]);

    useEffect(() => {
        if (selectedGroupId) {
            fetchTerminals(selectedGroupId);
        } else {
            setTerminals([]);
        }
    }, [selectedGroupId, fetchTerminals]);

    // --- Обработчики для групп ---
    const handleTabChange = (event: React.SyntheticEvent, newValue: number | 'new') => {
        if (newValue === 'new') {
            toggleGroupModal();
        } else {
            setSelectedGroupId(newValue as number);
        }
    };

    const handleNewGroupChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setNewGroup(prev => ({ ...prev, [name]: value }));
    };

    const handleEditingGroupChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (!editingGroup) return;
        const { name, value } = e.target;
        setEditingGroup({ ...editingGroup, [name]: value });
    };

    const handleCreateGroup = async (e: FormEvent) => {
        e.preventDefault();
        try {
            const response = await fetch('/api/management/groups', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newGroup)
            });
            if (!response.ok) {
                const errorMsg = await formatApiError(response, 'Не удалось создать группу');
                throw new Error(errorMsg);
            }

            showSuccess('Группа терминалов успешно создана');
            toggleGroupModal();
            setNewGroup(defaultNewGroup);

            const newGroupsList = await fetchGroups();
            if (newGroupsList.length > 0) {
                const latestGroup = newGroupsList.reduce((a: Group, b: Group) => a.id > b.id ? a : b);
                setSelectedGroupId(latestGroup.id);
            }
        } catch (error: any) {
            showError(error.message || 'Произошла ошибка при создании группы');
        }
    };

    const handleUpdateGroup = async (e: FormEvent) => {
        e.preventDefault();
        if (!editingGroup) return;
        try {
            const response = await fetch('/api/management/groups', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(editingGroup)
            });
            if (!response.ok) {
                const errorMsg = await formatApiError(response, 'Не удалось обновить группу');
                throw new Error(errorMsg);
            }
            showSuccess('Группа успешно обновлена');
            await fetchGroups();
            toggleEditGroupModal();
            setEditingGroup(null);
        } catch (error: any) {
            showError(error.message || 'Произошла ошибка при обновлении группы');
        }
    };
    
    const handleDeleteGroup = async () => {
        if (!editingGroup) return;
        setDeletingGroup(editingGroup);
        toggleEditGroupModal();
        toggleDeleteGroupModal();
    };
    
    const confirmDeleteGroup = async () => {
        if (!deletingGroup) return;
        try {
            const response = await fetch(`/api/management/groups/${deletingGroup.id}`, {
                method: 'DELETE'
            });
            if (!response.ok) {
                const errorMsg = await formatApiError(response, 'Не удалось удалить группу');
                throw new Error(errorMsg);
            }

            showSuccess('Группа успешно удалена');
            toggleDeleteGroupModal();
            const newSelectedGroupId = selectedGroupId === deletingGroup.id ? null : selectedGroupId;
            await fetchGroups();
            if (newSelectedGroupId === null && groups.length > 1) {
                 const remainingGroups = groups.filter(g => g.id !== deletingGroup.id);
                 setSelectedGroupId(remainingGroups.length > 0 ? remainingGroups[0].id : null);
            }
            setDeletingGroup(null);
        } catch (error: any) {
            showError(error.message || 'Произошла ошибка при удалении группы');
        }
    };

    const openEditGroupModal = (event: React.MouseEvent, group: Group) => {
        event.stopPropagation();
        setEditingGroup(group);
        toggleEditGroupModal();
    }

    // --- Обработчики для терминалов ---
    const handleNewTerminalChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>) => {
        const { name, value } = e.target;
        setNewTerminal(prev => ({ ...prev, [name]: value }));
    };
    
    const handleEditingTerminalChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<any>) => {
        if (!editingTerminal) return;
        const { name, value } = e.target;
        setEditingTerminal({ ...editingTerminal, [name]: value });
    };

    const handleCreateTerminal = async (e: FormEvent) => {
        e.preventDefault();
        if (!selectedGroupId) return;
        try {
            const terminalToCreate = {
                ...newTerminal,
                groupId: selectedGroupId,
                blocked: newTerminal.blocked ? parseInt(newTerminal.blocked, 10) : null,
                timeZone: parseInt(newTerminal.timeZone, 10),
            };
            const response = await fetch('/api/management/terminals', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(terminalToCreate)
            });
            if (!response.ok) {
                const errorMsg = await formatApiError(response, 'Не удалось создать терминал');
                throw new Error(errorMsg);
            }
            showSuccess('Терминал успешно создан');
            await fetchTerminals(selectedGroupId);
            toggleTerminalModal();
            setNewTerminal({ name: '', info: '', blocked: '', timeZone: '3' });
        } catch (error: any) {
            showError(error.message || 'Произошла ошибка при создании терминала');
        }
    };
    
    const handleUpdateTerminal = async (e: FormEvent) => {
        e.preventDefault();
        if (!editingTerminal) return;
        try {
            const terminalToUpdate = {
                ...editingTerminal,
                blocked: editingTerminal.blocked ? Number(editingTerminal.blocked) : null,
                timeZone: Number(editingTerminal.timeZone),
                internalId: Number(editingTerminal.internalId)
            };
            const response = await fetch('/api/management/terminals', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(terminalToUpdate)
            });
            if (!response.ok) {
                const errorMsg = await formatApiError(response, 'Не удалось обновить терминал');
                throw new Error(errorMsg);
            }
            showSuccess('Терминал успешно обновлен');
            if (selectedGroupId) {
                await fetchTerminals(selectedGroupId);
            }
            toggleEditTerminalModal();
            setEditingTerminal(null);
        } catch (error: any) {
            showError(error.message || 'Произошла ошибка при обновлении терминала');
        }
    };
    
    const handleDeleteTerminal = async () => {
        if (!deletingTerminal || !selectedGroupId) return;
        try {
            const response = await fetch(`/api/management/terminals/${deletingTerminal.id}`, {
                method: 'DELETE'
            });
            if (!response.ok) {
                const errorMsg = await formatApiError(response, 'Не удалось удалить терминал');
                throw new Error(errorMsg);
            }
            showSuccess('Терминал успешно удален');
            await fetchTerminals(selectedGroupId);
            toggleDeleteTerminalModal();
            setDeletingTerminal(null);
        } catch (error: any) {
            showError(error.message || 'Произошла ошибка при удалении терминала');
        }
    };

    const handleGenerateApiKey = async () => {
        if (!generatingKeyTerminal) return;
        try {
            const response = await fetch(`/api/management/terminals/${generatingKeyTerminal.id}/generate-apikey`, {
                method: 'POST'
            });
            if (!response.ok) {
                const errorMsg = await formatApiError(response, 'Не удалось сгенерировать API ключ');
                throw new Error(errorMsg);
            }
            const data = await response.json();
            const newApiKey = data.apiKey;

            setTerminals(prevTerminals =>
                prevTerminals.map(t =>
                    t.id === generatingKeyTerminal.id ? { ...t, apiKey: newApiKey } : t
                )
            );

            if (editingTerminal && editingTerminal.id === generatingKeyTerminal.id) {
                setEditingTerminal(prev => ({ ...prev!, apiKey: newApiKey }));
            }

            if (apiKeyTerminal && apiKeyTerminal.id === generatingKeyTerminal.id) {
                setApiKeyTerminal(prev => ({ ...prev!, apiKey: newApiKey }));
            }

            await navigator.clipboard.writeText(newApiKey);
            showSuccess('Новый API ключ сгенерирован и скопирован в буфер обмена');

        } catch (error: any) {
            showError(error.message || 'Произошла ошибка при генерации API ключа');
        } finally {
            toggleGenerateKeyModal();
            setGeneratingKeyTerminal(null);
        }
    };

    const copyToClipboard = (text: string) => {
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => {
            showSuccess('API ключ скопирован в буфер обмена');
        }, () => {
            showError('Не удалось скопировать API ключ в буфер обмена');
        });
    };
    
    const openEditTerminalModal = (terminal: Terminal) => {
        setEditingTerminal(terminal);
        toggleEditTerminalModal();
    }
    
    const openDeleteTerminalModal = (terminal: Terminal) => {
        setDeletingTerminal(terminal);
        toggleDeleteTerminalModal();
    }
    
    const openGenerateKeyModal = (terminal: Terminal) => {
        setGeneratingKeyTerminal(terminal);
        toggleGenerateKeyModal();
    }

    const openApiKeyModal = (terminal: Terminal) => {
        setApiKeyTerminal(terminal);
        toggleApiKeyModal();
    }
    
    const formatTimezone = (tz: number) => {
        if (tz === 0) return 'UTC';
        return `UTC${tz > 0 ? '+' : ''}${tz}`;
    }

    if (loading) {
        return <Loader />;
    }

    return (
        <Box sx={{ width: '100%' }}>
            <ToastNotification toast={toast} onClose={handleClose} />

            <Paper>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                        value={selectedGroupId}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        aria-label="group tabs"
                    >
                        {groups.map(group => (
                            <Tab 
                                key={group.id}
                                value={group.id}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {group.name}
                                        <IconButton 
                                            onClick={(e) => openEditGroupModal(e, group)}
                                            sx={{ ml: 1 }}
                                        >
                                            <Edit fontSize="small" />
                                        </IconButton>
                                    </Box>
                                } 
                            />
                        ))}
                        <Tab icon={<Add />} value="new" />
                    </Tabs>
                </Box>

                {selectedGroupId ? (
                    <Card>
                        <CardHeader
                            title="Терминалы"
                            subheader={`Группа: ${groups.find(g => g.id === selectedGroupId)?.name || ''} (ID: ${selectedGroupId})`}
                            action={
                                <Button onClick={toggleTerminalModal} startIcon={<Add />} variant="contained">Добавить терминал</Button>
                            }
                        />
                        <CardContent sx={{ pt: 0 }}>
                            {terminalsLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>ID</TableCell>
                                                <TableCell>Внутр. ID</TableCell>
                                                <TableCell>Название</TableCell>
                                                <TableCell>Инфо</TableCell>
                                                <TableCell>Блок.</TableCell>
                                                <TableCell>Часовой пояс</TableCell>
                                                <TableCell align="right">Действия</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {terminals.map(terminal => (
                                                <TableRow key={terminal.id} hover>
                                                    <TableCell>{terminal.id}</TableCell>
                                                    <TableCell>{terminal.internalId}</TableCell>
                                                    <TableCell>{terminal.name}</TableCell>
                                                    <TableCell>{terminal.info}</TableCell>
                                                    <TableCell>{terminal.blocked === 1 ? 'Да' : 'Нет'}</TableCell>
                                                    <TableCell>{formatTimezone(terminal.timeZone)}</TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title="API Ключ"><IconButton onClick={() => openApiKeyModal(terminal)}><VpnKey fontSize="small" /></IconButton></Tooltip>
                                                        <Tooltip title="Изменить"><IconButton onClick={() => openEditTerminalModal(terminal)}><Edit fontSize="small" /></IconButton></Tooltip>
                                                        <Tooltip title="Удалить"><IconButton onClick={() => openDeleteTerminalModal(terminal)} color="error"><Delete fontSize="small" /></IconButton></Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </CardContent>
                    </Card>
                ) : (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="h6">Нет групп для отображения</Typography>
                        <Typography>Создайте новую группу, чтобы начать добавлять терминалы.</Typography>
                        <Button onClick={toggleGroupModal} startIcon={<Add />} variant="contained" sx={{ mt: 2 }}>Создать группу</Button>
                    </Box>
                )}
            </Paper>

            {/* Модальные окна для групп */}
            <Dialog open={groupModal} onClose={toggleGroupModal} fullWidth maxWidth="sm">
                <DialogTitle>Создать новую группу</DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleCreateGroup} sx={{ mt: 2 }}>
                        <TextField label="Название группы" name="name" value={newGroup.name} onChange={handleNewGroupChange} required fullWidth margin="normal" />
                        <TextField label="Информация" name="info" value={newGroup.info} onChange={handleNewGroupChange} fullWidth margin="normal" />
                        <TextField label="Информация для печати" name="printInfo" value={newGroup.printInfo} onChange={handleNewGroupChange} fullWidth margin="normal" />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={toggleGroupModal}>Отмена</Button>
                    <Button onClick={handleCreateGroup} variant="contained" color="primary">Создать</Button>
                </DialogActions>
            </Dialog>

            {editingGroup && (
                <Dialog open={editGroupModal} onClose={toggleEditGroupModal} fullWidth maxWidth="sm">
                    <DialogTitle>Изменить группу</DialogTitle>
                    <DialogContent>
                        <Box component="form" onSubmit={handleUpdateGroup} sx={{ mt: 2 }}>
                            <TextField label="Название группы" name="name" value={editingGroup.name} onChange={handleEditingGroupChange} required fullWidth margin="normal" />
                            <TextField label="Информация" name="info" value={editingGroup.info} onChange={handleEditingGroupChange} fullWidth margin="normal" />
                            <TextField label="Информация для печати" name="printInfo" value={editingGroup.printInfo} onChange={handleEditingGroupChange} fullWidth margin="normal" />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'space-between' }}>
                        <Button onClick={handleDeleteGroup} color="error">Удалить группу</Button>
                        <Box>
                            <Button onClick={toggleEditGroupModal}>Отмена</Button>
                            <Button onClick={handleUpdateGroup} variant="contained" color="primary">Сохранить</Button>
                        </Box>
                    </DialogActions>
                </Dialog>
            )}
            
            {deletingGroup && (
                <Dialog open={deleteGroupModal} onClose={toggleDeleteGroupModal}>
                    <DialogTitle>Подтверждение удаления</DialogTitle>
                    <DialogContent>
                        <Typography>Вы уверены, что хотите удалить группу "<b>{deletingGroup.name}</b>"? Все терминалы в этой группе также будут удалены.</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={toggleDeleteGroupModal}>Отмена</Button>
                        <Button onClick={confirmDeleteGroup} color="error" variant="contained">Удалить</Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* Модальные окна для терминалов */}
            <Dialog open={terminalModal} onClose={toggleTerminalModal} fullWidth maxWidth="sm">
                <DialogTitle>Создать новый терминал</DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleCreateTerminal} sx={{ mt: 2 }}>
                        <TextField label="Название терминала" name="name" value={newTerminal.name} onChange={handleNewTerminalChange} required fullWidth margin="normal" />
                        <TextField label="Информация" name="info" value={newTerminal.info} onChange={handleNewTerminalChange} fullWidth margin="normal" />
                        <FormControl fullWidth margin="normal">
                            <InputLabel id="new-terminal-blocked-label">Заблокирован</InputLabel>
                            <Select labelId="new-terminal-blocked-label" name="blocked" value={newTerminal.blocked} label="Заблокирован" onChange={handleNewTerminalChange}>
                                <MenuItem value=""><em>Не указано</em></MenuItem>
                                <MenuItem value="1">Да</MenuItem>
                                <MenuItem value="0">Нет</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth margin="normal">
                            <InputLabel id="new-terminal-timezone-label">Часовой пояс</InputLabel>
                            <Select labelId="new-terminal-timezone-label" name="timeZone" value={newTerminal.timeZone} label="Часовой пояс" onChange={handleNewTerminalChange}>
                                {timezones.map(tz => <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={toggleTerminalModal}>Отмена</Button>
                    <Button onClick={handleCreateTerminal} variant="contained" color="primary">Создать</Button>
                </DialogActions>
            </Dialog>
            
            {editingTerminal && (
                <Dialog open={editTerminalModal} onClose={toggleEditTerminalModal} fullWidth maxWidth="sm">
                    <DialogTitle>Изменить терминал</DialogTitle>
                    <DialogContent>
                        <Box component="form" onSubmit={handleUpdateTerminal} sx={{ mt: 2 }}>
                            <TextField label="Название терминала" name="name" value={editingTerminal.name} onChange={handleEditingTerminalChange} required fullWidth margin="normal" />
                            <TextField label="Информация" name="info" value={editingTerminal.info} onChange={handleEditingTerminalChange} fullWidth margin="normal" />
                            <FormControl fullWidth margin="normal">
                                <InputLabel id="edit-terminal-blocked-label">Заблокирован</InputLabel>
                                <Select labelId="edit-terminal-blocked-label" name="blocked" value={editingTerminal.blocked ?? ''} label="Заблокирован" onChange={handleEditingTerminalChange}>
                                    <MenuItem value=""><em>Не указано</em></MenuItem>
                                    <MenuItem value={1}>Да</MenuItem>
                                    <MenuItem value={0}>Нет</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl fullWidth margin="normal">
                                <InputLabel id="edit-terminal-timezone-label">Часовой пояс</InputLabel>
                                <Select labelId="edit-terminal-timezone-label" name="timeZone" value={editingTerminal.timeZone} label="Часовой пояс" onChange={handleEditingTerminalChange}>
                                    {timezones.map(tz => <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <TextField label="Внутренний ID" name="internalId" type="number" value={editingTerminal.internalId} onChange={handleEditingTerminalChange} required fullWidth margin="normal" />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={toggleEditTerminalModal}>Отмена</Button>
                        <Button onClick={handleUpdateTerminal} variant="contained" color="primary">Сохранить</Button>
                    </DialogActions>
                </Dialog>
            )}

            {apiKeyTerminal && (
                <Dialog open={apiKeyModal} onClose={toggleApiKeyModal} fullWidth maxWidth="sm">
                    <DialogTitle>API Ключ для "{apiKeyTerminal.name}"</DialogTitle>
                    <DialogContent>
                        <TextField
                            label="API Ключ"
                            value={apiKeyTerminal.apiKey || ''}
                            fullWidth
                            margin="normal"
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <IconButton onClick={() => copyToClipboard(apiKeyTerminal.apiKey)} title="Копировать">
                                        <ContentCopy />
                                    </IconButton>
                                )
                            }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                        <Button color="secondary" variant="outlined" onClick={() => openGenerateKeyModal(apiKeyTerminal)}>Сгенерировать новый</Button>
                        <Button onClick={toggleApiKeyModal}>Закрыть</Button>
                    </DialogActions>
                </Dialog>
            )}
            
            {deletingTerminal && (
                <Dialog open={deleteTerminalModal} onClose={toggleDeleteTerminalModal}>
                    <DialogTitle>Подтверждение удаления</DialogTitle>
                    <DialogContent>
                        <Typography>Вы уверены, что хотите удалить терминал "<b>{deletingTerminal.name}</b>"?</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={toggleDeleteTerminalModal}>Отмена</Button>
                        <Button onClick={handleDeleteTerminal} color="error" variant="contained">Удалить</Button>
                    </DialogActions>
                </Dialog>
            )}

            {generatingKeyTerminal && (
                <Dialog open={generateKeyModal} onClose={toggleGenerateKeyModal}>
                    <DialogTitle>Сгенерировать новый API ключ?</DialogTitle>
                    <DialogContent>
                        <Typography>Вы уверены, что хотите сгенерировать новый API ключ для терминала "<b>{generatingKeyTerminal.name}</b>"?</Typography>
                        <Typography color="error" sx={{ mt: 1 }}><b>Старый ключ перестанет действовать немедленно.</b></Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={toggleGenerateKeyModal}>Отмена</Button>
                        <Button onClick={handleGenerateApiKey} color="primary" variant="contained">Сгенерировать</Button>
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    );
};
